import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ExternalLink, Menu, FileText, Download, Printer, LogIn, CheckCircle2,
  BookOpen, ArrowRight, ShieldCheck, Sparkles, Lightbulb, HelpCircle,
} from "lucide-react";

/**
 * Step-by-step guide for getting a credit-report PDF from each supported
 * source (Credit Karma, IdentityIQ, Experian, annualcreditreport.com).
 * Rendered inline as a button + modal. Fires an event that the parent
 * upload component can listen to when the user is ready to drop their PDF.
 */

const SOURCES = [
  {
    id: "credit_karma",
    name: "Credit Karma",
    tagline: "Free · Fastest · Best for beginners",
    accent: "from-emerald-500 to-teal-500",
    url: "https://www.creditkarma.com/my/credit-reports",
    difficulty: "Easy",
    time: "2 min",
    steps: [
      { icon: LogIn,   title: "Log in at creditkarma.com",             body: "Use the app or the website — either works." },
      { icon: Menu,    title: "Open the menu (☰) at the top left",     body: "On mobile it's the hamburger icon. On desktop look in the header." },
      { icon: FileText,title: "Tap 'Credit Report'",                    body: "You'll see two tabs — TransUnion and Equifax. Both are useful; grab whichever is easiest first." },
      { icon: Printer, title: "Tap 'Print' or 'Save'",                  body: "In the print dialog choose 'Save as PDF' as your destination. On iPhone use the Share sheet → 'Save to Files'." },
      { icon: Download,title: "You've got the PDF — come back here",    body: "Drop it in the upload box below. Our AI reads it in under 30 seconds." },
    ],
  },
  {
    id: "identityiq",
    name: "IdentityIQ",
    tagline: "3-bureau report in one file",
    accent: "from-violet-500 to-fuchsia-500",
    url: "https://member.identityiq.com/",
    difficulty: "Easy",
    time: "3 min",
    steps: [
      { icon: LogIn,    title: "Log in at member.identityiq.com",       body: "You need an active subscription for the 3-bureau report." },
      { icon: FileText, title: "Click 'Reports & Scores'",              body: "Then choose 'View my 3-Bureau Credit Report' — this is the one we want." },
      { icon: Printer,  title: "Click 'Print' at the top of the report",body: "In the print dialog select 'Save as PDF' as the destination and click Save." },
      { icon: Download, title: "You've got the PDF — come back here",   body: "Drop it below. This is the most complete report because it covers all 3 bureaus." },
    ],
  },
  {
    id: "experian",
    name: "Experian",
    tagline: "Official Experian FICO® score + report",
    accent: "from-sky-500 to-blue-500",
    url: "https://usa.experian.com/login/index",
    difficulty: "Medium",
    time: "5 min",
    steps: [
      { icon: LogIn,    title: "Log in at usa.experian.com",            body: "If you don't have an Experian account, create one (free tier available). Use your legal name and SSN." },
      { icon: FileText, title: "Go to 'Reports' → 'FICO Score & Credit Report'", body: "You may need to answer 3-4 identity-verification questions." },
      { icon: Download, title: "Scroll to the bottom and click 'Download PDF'", body: "If you don't see a Download link, use your browser's Print → Save as PDF instead." },
      { icon: Download, title: "You've got the PDF — come back here",   body: "This is the report Experian will use in most dispute conversations, so it's a great one to have." },
    ],
  },
  {
    id: "annual_credit_report",
    name: "annualcreditreport.com",
    tagline: "Federally free · Once per year per bureau",
    accent: "from-amber-500 to-orange-500",
    url: "https://www.annualcreditreport.com/",
    difficulty: "Medium",
    time: "10 min (identity verification)",
    steps: [
      { icon: LogIn,    title: "Visit annualcreditreport.com",           body: "This is the ONLY federally-authorized free source. All 3 bureaus (Experian, Equifax, TransUnion) once per year each." },
      { icon: FileText, title: "Click 'Request your free credit reports'",body: "Fill in your name, address, DOB, and SSN. You'll answer identity-verification questions from each bureau." },
      { icon: FileText, title: "Choose which bureau(s) to pull",         body: "You can grab all 3 at once. Save the PDF from EACH bureau separately." },
      { icon: Download, title: "Click 'Save' or 'Download PDF' on each report", body: "If a bureau redirects you to their own site, use Print → Save as PDF." },
      { icon: Download, title: "Upload all 3 PDFs here",                 body: "You can upload one at a time — our AI merges the data across reports automatically." },
    ],
  },
];

export default function CreditReportHowTo({ onReady }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(SOURCES[0].id);
  const active = SOURCES.find((s) => s.id === activeId) || SOURCES[0];

  return (
    <>
      {/* The evident CTA — the whole point is that this is impossible to miss */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full sm:w-auto inline-flex items-center gap-2 rounded-2xl px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-black shadow-lg shadow-amber-200 transition"
        data-testid="how-to-upload-credit-report"
      >
        <BookOpen size={18} />
        Don't know how? Watch the step-by-step guide
        <ArrowRight size={16} className="ml-1" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="credit-report-howto-dialog">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-lg shadow-amber-200 mb-2">
              <BookOpen size={26} strokeWidth={2.5} />
            </div>
            <DialogTitle className="text-center text-2xl sm:text-3xl font-display font-black text-slate-900">
              How to get your credit report PDF
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600 mt-1 text-base">
              Pick where you already have an account. If you don't have one anywhere,{" "}
              <b className="text-slate-800">annualcreditreport.com is free</b> and covers all 3 bureaus.
            </DialogDescription>
          </DialogHeader>

          {/* Source tabs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            {SOURCES.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`text-left rounded-xl p-3 border transition ${
                  s.id === activeId
                    ? `bg-gradient-to-br ${s.accent} text-white border-transparent shadow-md`
                    : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                }`}
                data-testid={`howto-tab-${s.id}`}
              >
                <p className="font-black text-sm truncate">{s.name}</p>
                <p className={`text-[10px] uppercase tracking-widest font-bold mt-0.5 ${s.id === activeId ? "text-white/80" : "text-slate-400"}`}>
                  {s.difficulty} · {s.time}
                </p>
              </button>
            ))}
          </div>

          {/* Active source detail */}
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`howto-content-${active.id}`}>
            <div className={`bg-gradient-to-r ${active.accent} px-5 py-4`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-white/80">
                    {active.difficulty} · {active.time}
                  </p>
                  <p className="text-white text-2xl font-display font-black leading-tight">{active.name}</p>
                  <p className="text-white/90 text-sm mt-0.5">{active.tagline}</p>
                </div>
                <a
                  href={active.url} target="_blank" rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-white/95 hover:bg-white text-slate-900 px-3 py-2 text-xs font-black shadow-md"
                  data-testid={`howto-open-${active.id}`}
                >
                  Open <ExternalLink size={12} />
                </a>
              </div>
            </div>

            <ol className="p-5 space-y-3">
              {active.steps.map((step, i) => {
                const Icon = step.icon || CheckCircle2;
                return (
                  <li key={i} className="flex items-start gap-3">
                    <div className={`w-9 h-9 shrink-0 rounded-xl bg-gradient-to-br ${active.accent} text-white flex items-center justify-center font-black text-sm shadow-sm`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className="text-slate-500 shrink-0" />
                        <p className="font-bold text-slate-900 text-sm sm:text-base">{step.title}</p>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{step.body}</p>
                    </div>
                  </li>
                );
              })}
            </ol>

            {/* Helpful sidebar tips */}
            <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <Lightbulb size={16} className="text-amber-500 mt-0.5 shrink-0" />
                <p className="text-slate-600">
                  <b className="text-slate-900">iPhone / iPad?</b> Use the Share sheet <span className="font-mono">↑</span> → <b>Save to Files</b> → choose Files → Save. The PDF is under Files → On my iPhone.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <ShieldCheck size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                <p className="text-slate-600">
                  <b className="text-slate-900">Your report is private.</b> Only you and your assigned Credit Quack specialist can see it. We never sell or share credit data.
                </p>
              </div>
            </div>
          </div>

          {/* FAQ / gotchas */}
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">
              <HelpCircle size={13} /> Common questions
            </p>
            <FaqRow q="Do I have to use a specific source?" a="No — any of the 4 above works. Credit Karma is fastest if you already have an account. annualcreditreport.com is the only truly free 3-bureau option." />
            <FaqRow q="What if my PDF is huge or a scan?" a="We support PDFs up to 15 MB. Screenshots and photos of paper reports work too, but a real PDF gives the most accurate AI parsing." />
            <FaqRow q="Do I have to upload all 3 bureaus?" a="No, one is fine to start. More reports = better insights, but even a single bureau unlocks your Funding Center." />
            <FaqRow q="What if I get stuck?" a="Message your specialist inside the app — they can walk you through it in real time." />
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-between items-center">
            <a
              href={active.url} target="_blank" rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 px-4 py-2.5 text-sm font-bold text-slate-800"
              data-testid="howto-primary-open"
            >
              <ExternalLink size={14} /> Open {active.name} now
            </a>
            <Button
              onClick={() => { setOpen(false); if (onReady) onReady(); }}
              className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-black"
              data-testid="howto-i-have-my-pdf"
            >
              <Sparkles size={14} className="mr-1" /> I have my PDF — take me to upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FaqRow({ q, a }) {
  return (
    <details className="group py-2 border-b last:border-b-0 border-slate-100">
      <summary className="cursor-pointer font-bold text-slate-900 text-sm flex items-center justify-between">
        {q}
        <span className="text-slate-400 group-open:rotate-180 transition text-lg leading-none">⌄</span>
      </summary>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a}</p>
    </details>
  );
}
